import { Link } from 'react-router-dom';
import type { TrainerCard } from '../../lib/types';
import { TrainerAvatar } from '../avatar/TrainerAvatar';
import { formatNumber, timeAgo } from '../../lib/format';

/**
 * The mini player card.
 *
 * One trainer, compact, with their face on it. This is what the Community page
 * lists people with, and it is meant to be reused anywhere else a person is
 * shown in a grid — search results, a guild's public page, a leaderboard that
 * wants faces rather than a table.
 *
 * ## What was wrong with the old one
 *
 * It was five stacked lines of text in a well: name, handle · title, three
 * counters, a timestamp, a status line. Nothing had a size relative to anything
 * else, so scanning twelve of them meant reading twelve of them — and the whole
 * point of a directory is that you *don't*. It also had no avatar, which is the
 * one thing that makes a stranger memorable.
 *
 * ## The shape
 *
 * Sprite on the left at 1x, identity to the right of it, and a thin footer with
 * the numbers. Three sizes of type doing three different jobs: the trainer's
 * name is the thing you read, the handle and title qualify it, and the counters
 * are there to be glanced at rather than read. The level sits on the sprite as a
 * corner tag rather than as a chip in the flow, because it is a property of the
 * person and not another line of the list.
 *
 * The whole card is one link. A card with a link inside a link is a card where
 * half of it does nothing, and there is only one place this can sensibly go.
 */

export interface PlayerCardProps {
  trainer: TrainerCard;
  /**
   * The "have I helped them today" footer.
   *
   * Only the Community page has an answer to this — a leaderboard or a search
   * result does not — so it is opt-in rather than always drawn. Not a quota:
   * there is no daily cap on feeding, so this reads as encouragement rather
   * than as a counter running down.
   */
  showHelped?: boolean;
}

export function PlayerCard({ trainer, showHelped = false }: PlayerCardProps) {
  return (
    <Link to={`/trainer/${trainer.username}`} className="pcard">
      <div className="pcard__top">
        <span className="pcard__portrait">
          {/* Exactly half the 80px source. 48 would be 0.6x, which resamples —
              the same rule the picker grid follows. */}
          <TrainerAvatar avatar={trainer.avatar} size={40} />
          <span className="pcard__level mono">{trainer.level}</span>
        </span>

        <span className="pcard__who">
          <span className="pcard__name">{trainer.trainerName}</span>
          <span className="pcard__handle tiny muted">@{trainer.username}</span>
          {trainer.title && <span className="pcard__title tiny">{trainer.title}</span>}
        </span>

        {/* Their Lead Pokemon — slot 1 of their Team. On the trainer's own
            side of the card rather than down with the counters, because it is
            part of who they are rather than a statistic about them. Absent
            entirely for a new trainer with an empty Team: an empty frame here
            would read as a missing image rather than as an empty Team. */}
        {trainer.lead && (
          <img
            className="pixel pcard__lead"
            src={trainer.lead.sprite}
            alt=""
            title={`${trainer.lead.name} · Lv ${trainer.lead.level}`}
            loading="lazy"
          />
        )}
      </div>

      {/* The numbers, as a row of glyph + value rather than labelled rows. At
          this size a label costs more space than the number it explains, and
          the icons are the same three used everywhere else in the game. */}
      <div className="pcard__stats">
        <span className="pcard__stat" title={`${formatNumber(trainer.totalHatched)} eggs hatched`}>
          ◓ {formatNumber(trainer.totalHatched)}
        </span>
        <span className="pcard__stat" title={`${formatNumber(trainer.totalFedOthers)} Pokémon fed for other trainers`}>
          ♥ {formatNumber(trainer.totalFedOthers)}
        </span>
        {trainer.shinyCount > 0 && (
          <span
            className="pcard__stat pcard__stat--shiny"
            title={`${formatNumber(trainer.shinyCount)} Shiny Pokémon`}
          >
            ✦ {formatNumber(trainer.shinyCount)}
          </span>
        )}
      </div>

      <div className="pcard__foot tiny">
        <span className="muted">{timeAgo(trainer.lastSeenAt)}</span>
        {showHelped && (
          <span className={trainer.helpedToday > 0 ? 'pcard__helped' : 'muted'}>
            {trainer.helpedToday > 0 ? `Helped ${trainer.helpedToday}×` : 'Not helped yet'}
          </span>
        )}
      </div>
    </Link>
  );
}
